import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import fetchApi from "api/fetchApi";
import IPosition from "models/Position";
import { IUser } from "models/Employee";
import { IDepartment } from "models/Department";
import { IRole } from "models/Role";

interface EmployeeRolesProps {
  selectedDepartment: number | string;
  setSelectedDepartment: (department: number | string) => void;
  lineManagerOption: IUser[];
  loadingLineManagers: boolean;
}

const EmployeeRoles = ({
  selectedDepartment,
  setSelectedDepartment,
  lineManagerOption,
  loadingLineManagers,
}: EmployeeRolesProps) => {
  const theme = useTheme();

  const { control } = useFormContext();
  const [positionOption, setPositionOption] = useState<IPosition[]>([]);
  const [departmentOption, setDepartmentOption] = useState<IDepartment[]>([]);
  const [roleOption, setRoleOption] = useState<IRole[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const positionResponse = await fetchApi(`job-titles`).get("");
        if (positionResponse.success) {
          setPositionOption(positionResponse.data.items);
        } else {
          console.log("Failed to fetch Positions");
        }

        const departmentResponse = await fetchApi(`departments`).get("");
        if (departmentResponse.success) {
          setDepartmentOption(departmentResponse.data.items);
        } else {
          console.log("Failed to fetch Departments");
        }

        const roleResponse = await fetchApi(`roles`).get("");
        if (roleResponse.success) {
          setRoleOption(roleResponse.data.items);
        } else {
          console.log("Failed to fetch Roles");
        }
      } catch (error) {
        console.log("An error occurred while fetching Data", error);
      }
    };
    fetchData();
  }, []);

  return (
    <form>
      <Box display="flex" flexDirection="column">
        <Typography
          variant="h4"
          sx={{
            fontSize: "14px",
            color: theme.palette.primary.main,
            fontWeight: 800,
            mt: 2,
            mb: 1,
          }}
        >
          Position
        </Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="position-label">Position</InputLabel>
          <Controller
            name="positions_id"
            control={control}
            render={({ field }) => (
              <Select
                name="positions_id"
                labelId="position-label"
                id="position"
                {...field}
                label="Position"
                sx={{ mb: 1 }}
              >
                {positionOption.map((position) => (
                  <MenuItem key={position.id} value={position.id}>
                    {position.title}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
        <Typography
          variant="h4"
          sx={{
            fontSize: "14px",
            color: theme.palette.primary.main,
            fontWeight: 800,
            mt: 2,
            mb: 1,
          }}
        >
          Department
        </Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="department-label">Department</InputLabel>
          <Controller
            name="departments_id"
            control={control}
            render={({ field }) => (
              <Select
                labelId="department-label"
                id="department"
                {...field}
                label="Department"
                sx={{ mb: 1 }}
                onChange={(event) => {
                  field.onChange(event);
                  setSelectedDepartment(event.target.value);
                }}
              >
                {departmentOption.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.title}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
        <Typography
          variant="h4"
          sx={{
            fontSize: "14px",
            color: theme.palette.primary.main,
            fontWeight: 800,
            mt: 2,
            mb: 1,
          }}
        >
          Role
        </Typography>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="role-label">Role</InputLabel>
          <Controller
            name="roles"
            control={control}
            render={({ field }) => (
              <Select labelId="role-label" id="role" {...field} label="Role" sx={{ mb: 1 }}>
                {roleOption.map((role) => (
                  <MenuItem key={role.id} value={role.name}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>

        <Typography
          variant="h4"
          sx={{
            fontSize: "14px",
            color: theme.palette.primary.main,
            fontWeight: 800,
            mt: 2,
            mb: 1,
          }}
        >
          Line Manager
        </Typography>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="lineManager-label">Line Manager</InputLabel>
          <Controller
            name="line_manager"
            control={control}
            render={({ field }) => (
              <Select
                labelId="lineManager-label"
                id="lineManager"
                {...field}
                label="Line Manager"
                sx={{ mb: 1 }}
              >
                {loadingLineManagers ? (
                  <MenuItem>
                    <CircularProgress size={20} />
                  </MenuItem>
                ) : (
                  lineManagerOption.map((manager) => (
                    <MenuItem key={manager.id} value={manager.id}>
                      {manager.first_name} {manager.last_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            )}
          />
        </FormControl>
      </Box>
    </form>
  );
};

export default EmployeeRoles;
